import { API } from "@swivl-mono/api-helper";
import { PhoneNumberHelper } from "@swivl-mono/helpers";
import {
  DM_EMAIL_TOKEN,
  DM_PHONE_TOKEN,
  FACILITY_EMAIL_TOKEN,
  FACILITY_PHONE_TOKEN,
  ForwardPhoneSource,
  LocationVarKey,
  forwardPhoneSource,
  forwardPhoneValue
} from "@swivl-mono/types";
import { PhoneInput } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { IoWarningOutline } from "react-icons/io5";
import { useLocation } from "react-router-dom";

import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { SegmentedToggle } from "../../../Views/Widgets/SegmentedToggle";

/**
 * Routing-group contact fields that can use Facility Info instead of a typed value
 * (ENG-3502). The stored value is a placeholder (`{{facility_phone}}`, `{{dm_phone}}`,
 * `{{dm_email}}`) that the server resolves per calling location at dial/send time.
 */

export interface FacilityContacts {
  facilityPhone?: string;
  dmPhone?: string;
  dmEmail?: string;
  facilityEmail?: string;
}

const SOURCE_LABELS: Record<ForwardPhoneSource, string> = { custom: "Custom Number", facility: "Facility Phone", dm: "DM Phone" };

const TOKEN_LABELS: Record<string, string> = {
  [FACILITY_PHONE_TOKEN]: "Facility Phone",
  [DM_PHONE_TOKEN]: "DM Phone",
  [FACILITY_EMAIL_TOKEN]: "Facility Email",
  [DM_EMAIL_TOKEN]: "DM Email"
};

/** "Facility Phone" / "DM Email" for a stored placeholder, else undefined. */
export function contactTokenLabel(value: string | null | undefined): string | undefined {
  return value ? TOKEN_LABELS[value] : undefined;
}

/** True for a stored placeholder; callers skip phone/email format checks for these. */
export function isContactToken(value: string | null | undefined): boolean {
  return contactTokenLabel(value) !== undefined;
}

/** Facility Info contacts at the page's scope, shown next to the choices so operators see what will be used. */
export function useFacilityContacts(): FacilityContacts {
  const orgId = Member.orgId!;
  const page = usePropertiesGetLocationIds();
  const [contacts, setContacts] = useState<FacilityContacts>({});
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const vars = await API.LocationVars.getVars(
          [
            LocationVarKey.CortexFacilityPhone,
            LocationVarKey.CortexFacilityDMPhone,
            LocationVarKey.CortexFacilityDMEmail,
            LocationVarKey.CortexFacilityEmail
          ],
          orgId,
          page.groupId ?? undefined,
          page.locationId ?? undefined
        );
        if (cancelled) return;
        setContacts({
          facilityPhone: vars[LocationVarKey.CortexFacilityPhone],
          dmPhone: vars[LocationVarKey.CortexFacilityDMPhone],
          dmEmail: vars[LocationVarKey.CortexFacilityDMEmail],
          facilityEmail: vars[LocationVarKey.CortexFacilityEmail]
        });
      } catch {
        // Display-only; the choices still work without the preview values.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, page.groupId, page.locationId]);
  return contacts;
}

/**
 * The Facility Info value behind a choice, or an inline warning linking to Facility
 * Info at the same scope. Opens in a new tab so unsaved flyout edits survive.
 */
interface FacilityValueProps {
  /** The Facility Info value behind the chosen source; undefined when it isn't set. */
  value: string | undefined;
}

export function FacilityValueHint(props: FacilityValueProps): React.ReactElement {
  const { pathname } = useLocation();
  const value = props.value?.trim();
  if (value) return <span className="text-sm text-grey-900">{value}</span>;
  // Sibling Details v3 page: swap the current page segment for facility-info.
  const facilityInfoHref = pathname.replace(/\/[^/]*$/, "/facility-info");
  return (
    <span className="flex items-center gap-1 text-sm text-yellow-800">
      <IoWarningOutline className="size-4 shrink-0" />
      <span>
        Not set on{" "}
        <a href={facilityInfoHref} target="_blank" rel="noreferrer" className="font-semibold underline hover:text-orange-500">
          Facility Info
        </a>
      </span>
    </span>
  );
}

interface ForwardNumberPickerProps {
  /** The stored value: a typed number or a Facility Info placeholder. */
  value: string;
  onChange: (next: string) => void;
  /** Choices to offer, in tab order. Without "custom" in the list, Custom Number comes first. */
  sources: ForwardPhoneSource[];
  contacts: FacilityContacts;
  error?: string | null;
  onBlur?: () => void;
  /** The choice an empty field starts on; saved values are never changed. */
  defaultSource?: ForwardPhoneSource;
}

/** A single forward number: typed, or the location's Facility Phone / DM Phone. */
export function ForwardNumberPicker(props: ForwardNumberPickerProps): React.ReactElement {
  const source = forwardPhoneSource(props.value);
  // Keeps the typed number while the operator flips between choices; PhoneInput
  // only reads its defaultValue on mount, so it's seeded from here.
  const [custom, setCustom] = useState(() => (source === "custom" ? props.value : ""));
  const chosenValue = source === "facility" ? props.contacts.facilityPhone : source === "dm" ? props.contacts.dmPhone : undefined;
  const options: ForwardPhoneSource[] = props.sources.includes("custom") ? props.sources : ["custom", ...props.sources];

  // An empty field (new setup) starts on the default choice. Mount-only, so a
  // number the operator clears later isn't swapped back.
  useEffect(() => {
    if (!props.value.trim() && props.defaultSource && props.defaultSource !== "custom") {
      props.onChange(forwardPhoneValue(props.defaultSource, ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSourceChange = (next: ForwardPhoneSource): void => props.onChange(forwardPhoneValue(next, custom));
  const handleCustomChange = (next: string): void => {
    setCustom(next);
    props.onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <SegmentedToggle<ForwardPhoneSource>
          options={options.map((s) => ({ value: s, label: SOURCE_LABELS[s] }))}
          value={source}
          onChange={handleSourceChange}
        />
      </div>
      {source === "custom" ? (
        <PhoneInput defaultValue={custom} onChange={handleCustomChange} onBlur={props.onBlur} error={props.error ?? undefined} />
      ) : (
        <div className="flex flex-col gap-1">
          <FacilityValueHint value={chosenValue} />
          <MultipleNumbersWarning value={chosenValue} />
        </div>
      )}
    </div>
  );
}

/**
 * A forwarded call rings one phone, so when the chosen Facility Info field holds several
 * numbers the server dials the first (same splitter as the server). Says so up front (Jenna).
 */
function MultipleNumbersWarning(props: FacilityValueProps): React.ReactElement | null {
  const numbers = PhoneNumberHelper.toDialableNumbers(props.value);
  if (numbers.length < 2) return null;
  return (
    <span className="flex items-center gap-1 text-sm text-yellow-800">
      <IoWarningOutline className="size-4 shrink-0" />
      Cannot forward to {numbers.length} numbers. {PhoneNumberHelper.formatPhoneNumberNational(numbers[0])} (the 1st listed) will be used.
    </span>
  );
}
